import {configure, action, decorate, observable, computed} from 'mobx';
import Analytics from 'shared/utils/analytics';

import {
  DEFAULT_LAYOUT_VIEW,
  FULL_LAYOUT_VIEW,
  MINI_LAYOUT_VIEW,
} from 'shared/constants';
import { stripCurrencySymbols } from '../components/project/shared';
import { toBoolean } from './utils';
import {removeLaneFromAuctionInLocalStorage} from '../shared/utils/localStorage';
import virtualAuctionStore from "./VirtualAuctionStore";

configure({ enforceActions: 'always' });

const audioPath = 'assets/sounds/sfx/'

const enable_sfx = true;

class UIStore {
  constructor(DataStore, MultiAuctionStore, VirtualAuctionStore) {
    this.dataStore = DataStore;
    this.multiAuctionStore = MultiAuctionStore;
    this.virtualAuctionStore = VirtualAuctionStore;
    this.initialize();
  }

  initialize = () => {
    this.isNative = window?.ReactNativeWebView;
    this.auctionStore = this.dataStore.auctionStore;
    this.hasPinnedNav = true;
    this.loading = false;
    this.scrollPosition = null;
    this.networkConnected = true;
    this.mobileNavOpen = false;
    this.showProdPreview = false;
    this.showRunList = true;
    this.activeLaneID = null;
    this.audioLane = null;
    this.audioAuction = null;
    this.activeAudioAuctionID = null;

    // this.layoutViewType = window.localStorage.getItem('layout') || DEFAULT_LAYOUT_VIEW;
    this.setProperty(
        'layoutViewType',
        window.localStorage.getItem('layout') || DEFAULT_LAYOUT_VIEW,
    );

    // Virtual Auctioneer Config
    this.voice = "kendall";
    this.isCountdownActive = false;
    this.isBonusTimeActive = false;
    this.isBiddingActive = false;
    this.countdownKey = 1;
    this.currentSound = null;
    this.duration = null;
    this.effectAudio = null;
    this.countdownAudioInstance = null;
    this.audioContext = null;
  };

  get isFullViewLayout() {
    return this.layoutViewType === FULL_LAYOUT_VIEW;
  }

  setProperty = (key, value) => {
    try {
      this[key] = value;
    } catch (error) {
      console.error('setProperty', { error, key, value });
    }
  };

  handleLogin = async (values) => {
    try {
      this.dataStore.connect(values.bidderNumber, values.name);
    } catch (error) {
      console.error('handleLogin', { error, values });
    }
  };

  handleJoinAuction = (auctionID) => {
    try {
      this.dataStore.sendMessage({
        method: 'JoinAuction',
        payload: [auctionID],
      });
    } catch (error) {
      console.error('handleJoinAuction', { error, auctionID });
    }
  };

  handleJoinLane = (auctionID, laneID) => {
    try {
      const bidStore = this.multiAuctionStore.getBidStore(auctionID);
      this.dataStore.sendMessage(auctionID, { method: 'JoinLane', payload: [laneID] });
      bidStore.setLaneRequestInProgress(true);
    } catch (error) {
      console.error('handleJoinLane', { error, laneID });
    }
  };

  handleExitLane = (auctionID, laneID) => {
    try {
      this.dataStore.sendMessage(auctionID, { method: 'ExitLane', payload: [laneID] });
      removeLaneFromAuctionInLocalStorage(auctionID, laneID);
    } catch (error) {
      console.error('handleExitLane', { error, laneID });
    }
  };

  placeLiveBid = (auctionID, laneID, syncID, bidAmount, isVirtual = false) => {
    try {
      if (isVirtual && this.virtualAuctionStore.soundPrefs.yourBid) {
        this.virtualAuctionStore.playEffect(`your_bid.mp3`);
      }
      this.dataStore.sendMessage(auctionID, {
        method: 'Bid',
        payload: [laneID, syncID, bidAmount],
      });

      const analyticsEventSuffix = toBoolean(this.isNative)
        ? ' - Native'
        : '';
      Analytics.RecordEvent(
        `Bid Button${analyticsEventSuffix}`,
        'Bidding',
        'Bid Amount',
        bidAmount,
      );
      if (this.isNative) {
        navigator.vibrate(200);
      }
    } catch (error) {
      console.error('placeLiveBid', {
        error,
        laneID,
        syncID,
        bidAmount,
      });
    }
  };

  handleDisconnect = () => {
    try {
      this.dataStore.closeConnection();
    } catch (error) {
      console.error('handleDisconnect', error);
    }
  };

  sendDirectMessage = async (auctionId, laneID, message, userType = 'Bidder') => {
    try {
      const messageStore = this.multiAuctionStore.getMessageStore(auctionId);
      const bidStore = this.multiAuctionStore.getBidStore(auctionId);
      if (laneID && message) {
        const payload = {
          message,
          lotNumber: bidStore.auction[laneID].lotNumber || null,
          userType,
        };
        messageStore.insertDirectMessageMap(laneID, payload);
        await this.dataStore.sendMessage(auctionId, {
          method: 'MessageToClerks',
          payload: [laneID, message, payload.lotNumber, payload.userType],
        });
      }
    } catch (error) {
      console.error('sendDirectMessage', {
        error,
        laneID,
        message,
        userType,
      });
    }
  };

  sendSellerMessage = async (auctionID, laneID, sellerAction, sellerActionAmount) => {
    try {
      if (laneID && sellerAction) {
        //Analytics.RecordEvent('Seller Actions', 'Seller', 'Button Action', sellerAction);
        Analytics.RecordEvent(
          `Seller Action - ${sellerAction}`,
          'Seller',
          'Button Action',
          sellerAction,
        );

        await this.dataStore.sendMessage(auctionID, {
          method: 'SellerAction',
          payload: [laneID, sellerAction, sellerActionAmount],
        });
      }
    } catch (error) {
      console.error('sendSellerMessage', {
        error,
        laneID,
        sellerAction,
        sellerActionAmount,
      });
    }
  };

  setTempSellerAction = (
    auctionID,
    laneID,
    tempSellerAction,
    tempSellerActionAmount = null,
  ) => {
    const bidStore = this.multiAuctionStore.getBidStore(auctionID);
    bidStore.auction[laneID].seller.tempSellerAction = tempSellerAction;
    bidStore.auction[
      laneID
    ].seller.tempTakeAmount = tempSellerActionAmount;
    if (!bidStore.auction[laneID].seller.sellerActionAmount) {
      bidStore.auction[
        laneID
      ].seller.sellerActionAmount = tempSellerActionAmount;
    }
  };

  clearTempSellerAction = (auctionID, laneID) => {
    try {
      const bidStore = this.multiAuctionStore.getBidStore(auctionID);
      bidStore.auction[laneID].seller.tempSellerAction = null;
      bidStore.auction[laneID].seller.tempTakeAmount = null;
    }
    catch (error) {
      console.log(error);
    }
  };

  setSellerAction = (
    auctionID,
    laneID,
    sellerAction,
    sellerActionAmount = null,
    tempTakeAmount = null,
  ) => {
    this.clearTempSellerAction(auctionID, laneID);
    if (
      sellerAction !== 'TAKES' ||
      (sellerAction === 'TAKES' && sellerActionAmount)
    ) {
      this.sendSellerMessage(auctionID, laneID, sellerAction, sellerActionAmount);
    }
  };

  clearSellerAction = (auctionID, laneID, sellerAction, userInitiated = true) => {
    // console.log('clearSellerAction', sellerAction);
    if (userInitiated) {
      switch (sellerAction) {
        case 'SELL':
          this.sendSellerMessage(auctionID, laneID, 'CANCEL SELL');
          break;
        case 'TAKES':
          this.sendSellerMessage(auctionID, laneID, 'CANCEL TAKES');
          break;
        case 'PASS':
          this.sendSellerMessage(auctionID, laneID, 'CANCEL PASS');
          break;
        default:
        // code block
      }
    }


    const bidStore = this.multiAuctionStore.getBidStore(auctionID);
    bidStore.setAuctionProperty(laneID, 'seller', {
      message: null,
      tempTakeAmount: null,
      sellerAction: null,
      sellerActionAmount: null,
      sellerActionAccount: null,
      sellerActionAccountName: null,
      sellerAccount: null,
      sellerAccountName: null,
    });
  };

  setIsPinnedNav = (value) => {
    this.hasPinnedNav = value;
  };

  toggleLoading = () => {
    this.loading = !this.loading;
  };

  handleSetLaneAudio = (auctionID, laneID, value, userInitiated) => {
    try {
      const bidStore = this.multiAuctionStore.getBidStore(auctionID);
      if (bidStore) {
        this.virtualAuctionStore.setCurrentLaneAudio(laneID, value);
        bidStore.setCurrentLaneAudio(laneID, value, userInitiated);
        this.activeLaneID = laneID;
        this.audioLane = laneID;
        this.audioAuction = auctionID;
      }
    }
    catch (error) {
      console.log(error);
    }
  }

  clearRunListFilter = (auctionID, laneId) => {
    const bidStore = this.multiAuctionStore.getBidStore(auctionID);
    bidStore.handleRunListFilter(laneId, '', '');
  }

  setLayoutViewType = (viewType) => {
    const isValidViewType = [
      DEFAULT_LAYOUT_VIEW,
      MINI_LAYOUT_VIEW,
      FULL_LAYOUT_VIEW,
    ].includes(viewType);
    if (!isValidViewType) console.error('Invalid layout view type');
    this.setProperty('layoutViewType', viewType);
    window.localStorage.setItem('layout', viewType);


    if (viewType === DEFAULT_LAYOUT_VIEW || viewType === FULL_LAYOUT_VIEW) {
        this.showRunList = true;
        this.showProdPreview = false;
      } else if (viewType === MINI_LAYOUT_VIEW) {
        this.showRunList = false;
        this.showProdPreview = false;
      }
  };

  setScrollPosition = (value) => {
    this.scrollPosition = value;
  };

  setNetworkConnected = (value) => {
    this.networkConnected = value;
  };

  handleCurrencyKeyUpFormat = (e) => {
    const numericValues = stripCurrencySymbols(e.currentTarget.value);
    e.currentTarget.value = numericValues;
  };

  handleTriggerFinancialCommitments = (auctionID) => {
    try {
      this.dataStore.sendMessage(auctionID, {
        method: 'GetFinancialCommitments',
        payload: [auctionID],
      });
    } catch (error) {
      console.error('handleTriggerFinancialCommitments', { error, auctionID });
    }
  };

  toggleMobileNav = (value) => {
    this.mobileNavOpen = value;
  };
}

decorate(UIStore, {
  clearRunListFilter: action,
  clearSellerAction: action,
  clearTempSellerAction: action,
  handleCurrencyKeyUpFormat: action,
  handleDisconnect: action,
  handleExitLane: action,
  handleJoinAuction: action,
  handleJoinLane: action,
  handleLogin: action,
  handleRunListFilter: action,
  handleSetLaneAudio: action,
  handleTriggerFinancialCommitments: action,
  hasPinnedNav: observable,
  initialize: action,
  loading: observable,
  mobileNavOpen: observable,
  networkConnected: observable,
  isNative: observable,
  layoutViewType: observable,
  audioLane: observable,
  audioAuction: observable,
  placeLiveBid: action,
  resetRunListFilter: action,
  scrollPosition: observable,
  showProdPreview: observable,
  showRunList: observable,
  sendDirectMessage: action,
  sendSellerMessage: action,
  setIsPinnedNav: action,
  setLayoutViewType: action,
  setNetworkConnected: action,
  setScrollPosition: action,
  setSellerAction: action,
  setTempSellerAction: action,
  toggleLoading: action,
  toggleMobileNav: action,
  isFullViewLayout: computed,
});

export default UIStore;
